import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { cn } from "@/dentlab/src/lib/util";
import {
	ShoppingBasket,
	Plus,
	Archive,
	ChevronDown,
	ChevronRight,
} from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { Tables } from "@/lib/supabase";
import { useConnectStore } from "@/dentlab/src/store/Connect";
import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { JobDocumentTypeEnum } from "../../job-document/job-document.types";
import { JobDocumentSidebarItem } from "./job-document-sidebar-item.component";
import { JobDocumentSidebarCollapsedItem } from "./job-document-sidebar-collapsed-item.component";
import { JobDocumentCreationGrid } from "./job-document-creation-grid.component";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/dentlab/src/components/ui/popover";
import {
	Collapsible,
	CollapsibleTrigger,
	CollapsibleContent,
} from "@/dentlab/src/components/ui/collapsible";
import { SIDEBAR_COLLAPSED_WIDTH } from "../job-page.constants";
import { SidebarCollapseToggle } from "../sidebar-collapse-toggle.component";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";

const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

interface JobDocumentSidebarProps {
	sortedDocuments: JobDocumentWithFiles[]; // the parent component already sorts the documents so we don't need to do it again here
	childJobChannels: Tables<"message_channels">[] | null;
	selectedDocumentId: number | null;
	onSelectDocument: (documentId: number | null) => void;
	onOpenVendorDialog: () => void;
	onFileClick: (fileId: string) => void;
	onCreateDocument: (type: JobDocumentTypeEnum) => void;
	onCreateFromTemplate: (type: JobDocumentTypeEnum) => void;
	onChildJobChannelClick: (channelId: string) => void;
}

export const JobDocumentSidebar: React.FC<JobDocumentSidebarProps> = ({
	sortedDocuments,
	childJobChannels,
	selectedDocumentId,
	onSelectDocument,
	onOpenVendorDialog,
	onFileClick,
	onCreateDocument,
	onCreateFromTemplate,
	onChildJobChannelClick,
}) => {
	const [width, setWidth] = useState(() =>
		getLocalStorage("job-document-sidebar-width", DEFAULT_WIDTH)
	);
	const [collapsed, setCollapsed] = useState(() =>
		getLocalStorage("job-document-sidebar-collapsed", false)
	);
	const [isResizing, setIsResizing] = useState(false);
	const [creationPopoverOpen, setCreationPopoverOpen] = useState(false);
	const [archivedSectionCollapsed, setArchivedSectionCollapsed] = useState(
		() => getLocalStorage("job-document-sidebar-archived-collapsed", true)
	);
	const sidebarRef = useRef<HTMLDivElement>(null);
	const selectedItemRef = useRef<HTMLDivElement>(null);

	const { brandsByOrgId } = useConnectStore((state) => ({
		brandsByOrgId: state.brandsByOrgId,
	}));

	const { activeDocuments, archivedDocuments } = useMemo(() => {
		return {
			activeDocuments: sortedDocuments.filter(
				(doc) => doc.status !== JobStatusEnum.ARCHIVED
			),
			archivedDocuments: sortedDocuments.filter(
				(doc) => doc.status === JobStatusEnum.ARCHIVED
			),
		};
	}, [sortedDocuments]);

	useEffect(() => {
		if (selectedItemRef.current) {
			selectedItemRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [selectedDocumentId]);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setIsResizing(true);
	}, []);

	useEffect(() => {
		if (!isResizing) return;

		const handleMouseMove = (e: MouseEvent) => {
			if (!sidebarRef.current) return;
			const rect = sidebarRef.current.getBoundingClientRect();
			const newWidth = e.clientX - rect.left;
			const clampedWidth = Math.min(
				Math.max(newWidth, MIN_WIDTH),
				MAX_WIDTH
			);
			setWidth(clampedWidth);
			setLocalStorage("job-document-sidebar-width", clampedWidth);
		};

		const handleMouseUp = () => {
			setIsResizing(false);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [isResizing]);

	const toggleCollapse = useCallback(() => {
		setCollapsed((prev) => {
			const newValue = !prev;
			setLocalStorage("job-document-sidebar-collapsed", newValue);
			return newValue;
		});
	}, []);

	const handleCreationAction = useCallback((action: () => void) => {
		action();
		setCreationPopoverOpen(false);
	}, []);

	if (collapsed) {
		return (
			<div
				className="flex flex-col h-full border-r bg-gray-50"
				style={{ width: SIDEBAR_COLLAPSED_WIDTH }}
				data-testid="job-document-sidebar"
			>
				<div className="border-b flex items-center py-1 px-2">
					<Popover
						open={creationPopoverOpen}
						onOpenChange={setCreationPopoverOpen}
					>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className="h-8 w-8"
								data-testid="job-document-create-menu"
								aria-label="Neues Dokument erstellen"
							>
								<Plus className="h-4 w-4" />
							</Button>
						</PopoverTrigger>
						<PopoverContent
							side="right"
							align="start"
							className="w-auto p-0"
						>
							<JobDocumentCreationGrid
								onCreateDocument={(type) =>
									handleCreationAction(() =>
										onCreateDocument(type)
									)
								}
								onCreateFromTemplate={(type) =>
									handleCreationAction(() =>
										onCreateFromTemplate(type)
									)
								}
								onOpenVendorDialog={() =>
									handleCreationAction(onOpenVendorDialog)
								}
							/>
						</PopoverContent>
					</Popover>
				</div>

				<div className="flex-1 overflow-y-auto flex flex-col items-center px-1 pt-1">
					{childJobChannels && childJobChannels.length > 0 && (
						<>
							{childJobChannels.map((channel) => {
								const vendorName = channel.vendor_org_id
									? brandsByOrgId[channel.vendor_org_id]
											?.name || "Unbekanntes Fremdlabor"
									: "Unbekanntes Fremdlabor";

								return (
									<TooltipWrapper
										key={channel.id}
										title={vendorName}
										position="right"
									>
										<button
											onClick={() =>
												channel.id &&
												onChildJobChannelClick(
													channel.id
												)
											}
											className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
											data-testid={`job-document-sidebar-fremdlabor-${channel.id}`}
											aria-label={vendorName}
										>
											<ShoppingBasket className="h-4 w-4 text-gray-600" />
										</button>
									</TooltipWrapper>
								);
							})}
							<div className="py-2" />
						</>
					)}

					{activeDocuments.map((doc) => (
						<div
							key={doc.id}
							ref={
								selectedDocumentId === doc.id
									? selectedItemRef
									: undefined
							}
							className="py-0.5"
						>
							<JobDocumentSidebarCollapsedItem
								document={doc}
								isSelected={selectedDocumentId === doc.id}
								onSelect={onSelectDocument}
							/>
						</div>
					))}

					{archivedDocuments.length > 0 && (
						<>
							<div className="w-6 border-t border-gray-300 my-2" />
							{archivedDocuments.map((doc) => (
								<div
									key={doc.id}
									ref={
										selectedDocumentId === doc.id
											? selectedItemRef
											: undefined
									}
									className="py-0.5"
								>
									<JobDocumentSidebarCollapsedItem
										document={doc}
										isSelected={
											selectedDocumentId === doc.id
										}
										onSelect={onSelectDocument}
										isArchived
									/>
								</div>
							))}
						</>
					)}
				</div>

				<SidebarCollapseToggle
					isCollapsed={collapsed}
					onToggle={toggleCollapse}
					testId="job-document-sidebar-toggle"
				/>
			</div>
		);
	}

	return (
		<div
			ref={sidebarRef}
			className="flex flex-col h-full border-r bg-gray-50 relative"
			style={{ width, minWidth: MIN_WIDTH, maxWidth: MAX_WIDTH }}
			data-testid="job-document-sidebar"
		>
			<div className="border-b">
				<JobDocumentCreationGrid
					onCreateDocument={onCreateDocument}
					onCreateFromTemplate={onCreateFromTemplate}
					onOpenVendorDialog={onOpenVendorDialog}
				/>
			</div>

			<div className="flex-1 overflow-y-auto">
				{childJobChannels && childJobChannels.length > 0 && (
					<div className="py-2">
						{childJobChannels.map((channel) => {
							const vendorName = channel.vendor_org_id
								? brandsByOrgId[channel.vendor_org_id]?.name ||
									"Unbekanntes Fremdlabor"
								: "Unbekanntes Fremdlabor";

							return (
								<button
									key={channel.id}
									onClick={() =>
										channel.id &&
										onChildJobChannelClick(channel.id)
									}
									className="flex items-center gap-2 w-full px-2 py-1.5 text-left hover:bg-gray-100 rounded-sm mx-1"
									style={{ width: "calc(100% - 8px)" }}
									data-testid={`job-document-sidebar-fremdlabor-${channel.id}`}
									aria-label={`${vendorName} - ${
										channel.job_status
											? jobStatusToLabel[
													channel.job_status as JobStatusEnum
												]
											: "Unbekannt"
									}`}
								>
									<ShoppingBasket className="h-4 w-4 text-gray-600 flex-shrink-0" />
									<span className="text-sm font-medium truncate flex-1">
										{vendorName}
									</span>
									<span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
										{channel.job_status
											? jobStatusToLabel[
													channel.job_status as JobStatusEnum
												]
											: "Unbekannt"}
									</span>
								</button>
							);
						})}
					</div>
				)}

				{activeDocuments.length > 0 && (
					<div
						className={cn(
							"py-2",
							childJobChannels &&
								childJobChannels.length > 0 &&
								"border-t"
						)}
					>
						{activeDocuments.map((doc) => (
							<div
								key={doc.id}
								ref={
									selectedDocumentId === doc.id
										? selectedItemRef
										: undefined
								}
							>
								<JobDocumentSidebarItem
									document={doc}
									isSelected={selectedDocumentId === doc.id}
									onSelect={onSelectDocument}
									onFileClick={onFileClick}
								/>
							</div>
						))}
					</div>
				)}

				{archivedDocuments.length > 0 && (
					<Collapsible
						open={!archivedSectionCollapsed}
						onOpenChange={(open) => {
							setArchivedSectionCollapsed(!open);
							setLocalStorage(
								"job-document-sidebar-archived-collapsed",
								!open
							);
						}}
						className="mt-2 pt-0 pb-0 border-none"
					>
						<CollapsibleTrigger asChild>
							<button
								className="flex items-center gap-1 w-full px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
								data-testid="job-document-sidebar-archived-toggle"
								aria-label={`Archivierte Dokumente ${
									archivedSectionCollapsed
										? "anzeigen"
										: "ausblenden"
								}`}
							>
								{archivedSectionCollapsed ? (
									<ChevronRight className="h-4 w-4" />
								) : (
									<ChevronDown className="h-4 w-4" />
								)}
								<span>Ungültig/Archiviert</span>
								<span className="text-xs text-gray-600 rounded-full pr-1">
									({archivedDocuments.length})
								</span>
							</button>
						</CollapsibleTrigger>
						<CollapsibleContent className="px-0 py-0.5">
							{archivedDocuments.map((doc) => (
								<div
									key={doc.id}
									ref={
										selectedDocumentId === doc.id
											? selectedItemRef
											: undefined
									}
									className="opacity-60"
								>
									<JobDocumentSidebarItem
										document={doc}
										isSelected={
											selectedDocumentId === doc.id
										}
										onSelect={onSelectDocument}
										onFileClick={onFileClick}
									/>
								</div>
							))}
						</CollapsibleContent>
					</Collapsible>
				)}
			</div>

			<SidebarCollapseToggle
				isCollapsed={collapsed}
				onToggle={toggleCollapse}
				testId="job-document-sidebar-toggle"
			/>

			<div
				onMouseDown={handleMouseDown}
				className={cn(
					"absolute top-0 right-0 w-1 h-full cursor-col-resize",
					"hover:bg-blue-300 transition-colors",
					isResizing && "bg-blue-400"
				)}
				data-testid="job-document-sidebar-resize-handle"
				aria-label="Seitenleiste Größe ändern"
			/>
		</div>
	);
};
